import type { RouterOptions } from '@nuxt/schema'

// a page-group's main page (`/[slug]`) and its nested pages (`/[slug]/[...path]`)
const pageGroupMainRouteName = 'slug'
const nestedPageRouteName = 'slug-path'
const pageGroupRouteNames = [pageGroupMainRouteName, nestedPageRouteName]

const pathSegments = (route: any): string[] => {
  const path = route?.params?.path
  return Array.isArray(path) ? path : path ? [path] : []
}

const isStrictPrefixOf = (a: string[], b: string[]) =>
  a.length < b.length && a.every((segment, index) => segment === b[index])

// pages within the same page-group share the `slug` param (the group's main
// page); `path` then encodes the level/child. Moving up means the page being
// opened is an ancestor of the one being left — its path is a strict prefix.
const isMoveToPageGroupAncestor = (from: any, to: any, fromName: string, toName: string) => {
  if (!pageGroupRouteNames.includes(fromName) || !pageGroupRouteNames.includes(toName)) { return false }
  if (from?.params?.slug !== to?.params?.slug) { return false }

  return isStrictPrefixOf(pathSegments(to), pathSegments(from))
}

const scrollIfNeeded = (to: any, from: any) => {
  if (to?.path === from?.path) { return }

  const getRouteBaseName = useRouteBaseName()
  const fromName = String(getRouteBaseName(from) ?? '')
  const toName = String(getRouteBaseName(to) ?? '')

  // an overlay detail page opens on top of its own list, which stays mounted
  // underneath and so keeps the position it was scrolled to — both when opening
  // one from the list and when switching between two of them. Arriving from
  // anywhere else there is no list underneath, so it starts from the top
  if (
    overlayDetailNames.has(toName) &&
    (detailToListName[toName] === fromName || fromName === toName)
  ) { return }

  // closing an overlay detail: its list stayed mounted underneath and kept the
  // position it was at, so scrolling it again only risks a jump
  if (overlayDetailNames.has(fromName) && detailToListName[fromName] === toName) { return }

  // detail page -> its own list: restore the list's scroll position from
  // before it was left, rather than scrolling to top
  const listName = detailToListName[fromName]
  if (listName && listName === toName) {
    return listScrollPositions.get(listName) ?? { left: 0, top: 0 }
  }

  // moving back up to an ancestor within one page-group: that page was already
  // visited, so it keeps the position it was left at. Every other move —
  // descending into a nested page, a sibling, or a page in another group —
  // opens a page the visitor hasn't seen yet, which starts from the top
  if (isMoveToPageGroupAncestor(from, to, fromName, toName)) { return }

  return { left: 0, top: 0 }
}

export default <RouterOptions> {
  scrollBehavior (to, from) {
    const nuxtApp = useNuxtApp()

    // in-page move: the page stays mounted, so `page:finish` never fires. A
    // hash is scrolled to right away (smoothly, as an anchor click would); any
    // other in-page change leaves the position alone
    if (to.path === from.path) {
      if (to.hash) { scrollToHash(to.hash) }
      return false
    }

    return new Promise((resolve) => {
      nuxtApp.hooks.hookOnce('page:finish', () => {
        // arriving on a page with a hash: land on the target directly (as the
        // browser would on load). The element may only render after the route
        // resolved, so this is delegated rather than returned as `{ el }`,
        // which vue-router would give up on if the element isn't there yet
        if (to.hash) {
          scrollToHash(to.hash, { behavior: 'auto' })
          resolve(false)
          return
        }

        cancelPendingHashScroll()
        resolve({
          ...scrollIfNeeded(to, from)
        })
      })
    })
  }
}
